import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { StatusBadge, type Status } from '../components/StatusBadge'
import { ProcessBadge } from '../components/ProcessBadge'

interface IdeaAuthor {
  full_name: string
  position?: string | null
  business_unit_name?: string | null
  department_name?: string | null
  employee_number?: string | null
}

interface IdeaListItem {
  id: string
  code: string
  title: string
  description: string
  status: Status
  ai_status: string
  version: number
  author?: IdeaAuthor | null
  target_department_name?: string | null
  process_tags?: string[]
  created_at: string
  updated_at: string
}

export const Route = createFileRoute('/inbox')({
  beforeLoad: async () => {
    const s = useAuth.getState()
    if (s.loading) await s.load()
    const u = useAuth.getState().user
    if (!u) throw redirect({ to: '/login' })
    if (u.role === 'user') throw redirect({ to: '/my-ideas' })
  },
  component: InboxPage,
})

function InboxPage() {
  const { data: ideas } = useQuery({
    queryKey: ['inbox-ideas'],
    queryFn: () => api<IdeaListItem[]>('/api/ideas?limit=200'),
  })
  const { data: statuses } = useQuery({
    queryKey: ['lookups', 'statuses'],
    queryFn: () => api<Status[]>('/api/lookups/statuses'),
  })

  if (!ideas || !statuses) {
    return <p className="p-8 text-sm text-zinc-500">Загрузка…</p>
  }

  return (
    <div className="max-w-7xl mx-auto p-8 space-y-4">
      <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
        Входящие идеи ({ideas.length})
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {statuses.map((status) => {
          const col = ideas.filter((i) => i.status.id === status.id)
          return (
            <div
              key={status.id}
              className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-3 min-h-[200px]"
            >
              <div className="flex items-center justify-between mb-3">
                <StatusBadge status={status} />
                <span className="text-xs text-zinc-500">{col.length}</span>
              </div>
              <div className="space-y-2">
                {col.map((i) => (
                  <Link
                    key={i.id}
                    to="/ideas/$id"
                    params={{ id: i.id }}
                    className="block bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 p-2 hover:border-amber-400 transition"
                  >
                    <p className="text-xs font-mono text-zinc-500">{i.code}</p>
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 line-clamp-2 mt-0.5">
                      {i.title}
                    </p>
                    {i.author && (
                      <p className="text-xs text-zinc-500 mt-1 truncate">
                        👤 {i.author.full_name}
                      </p>
                    )}
                    {i.author?.position && (
                      <p className="text-xs text-zinc-500 truncate">
                        {i.author.position}
                      </p>
                    )}
                    {i.target_department_name && (
                      <p className="text-xs text-zinc-500 truncate">
                        🏭 {i.target_department_name}
                      </p>
                    )}
                    {i.process_tags && i.process_tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {i.process_tags.map((code) => (
                          <ProcessBadge key={code} code={code} />
                        ))}
                      </div>
                    )}
                    {i.ai_status === 'pending' && (
                      <p className="text-xs text-amber-600 mt-1">🤖 AI…</p>
                    )}
                    {i.ai_status === 'done' && (
                      <p className="text-xs text-green-600 mt-1">🤖 готово</p>
                    )}
                  </Link>
                ))}
                {col.length === 0 && (
                  <p className="text-xs text-zinc-400 text-center py-4">пусто</p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
